// NEW_FILE_JUSTIFIED: Frontend settings page for the automation feature; convex/automation/ contains the backend logic
import { Zap, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { useAutomationSettings } from '@/hooks/useAutomationSettings';
import type { IFilterRuleRowProps } from '@/@types/automation';

function FilterRuleRow({ rule, index, onUpdate, onDelete }: IFilterRuleRowProps) {
  const ruleDescription =
    rule.senderEmail?.trim() || rule.subjectPattern?.trim() || `rule ${index + 1}`;
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor={`sender-${index}`} className="text-xs text-muted-foreground">
                Sender Email
              </Label>
              <Input
                id={`sender-${index}`}
                placeholder="e.g. leads@company.com"
                value={rule.senderEmail ?? ''}
                onChange={e => onUpdate(index, { ...rule, senderEmail: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`subject-${index}`} className="text-xs text-muted-foreground">
                Subject Pattern
              </Label>
              <Input
                id={`subject-${index}`}
                placeholder="e.g. New lead:"
                value={rule.subjectPattern ?? ''}
                onChange={e => onUpdate(index, { ...rule, subjectPattern: e.target.value })}
              />
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="mt-6 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(index)}
            aria-label={`Delete filter: ${ruleDescription}`}
          >
            <Trash2 aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function AutomationSettingsPage() {
  const {
    autoCreateLeads,
    filters,
    isSaving,
    isDirty,
    isLoading,
    hasFilters,
    setAutoCreate,
    addFilter,
    updateFilter,
    deleteFilter,
    save,
  } = useAutomationSettings();

  if (isLoading) {
    return (
      <div role="status" className="flex-1 flex items-center justify-center p-6">
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center gap-2">
          <Zap aria-hidden="true" className="h-6 w-6 text-foreground" />
          <h1 className="text-2xl font-bold text-foreground">Automation</h1>
        </div>

        <Card>
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-3">
              <Checkbox
                id="auto-create-leads"
                checked={autoCreateLeads}
                disabled={!hasFilters && !autoCreateLeads}
                onCheckedChange={checked => setAutoCreate(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="auto-create-leads" className="text-sm font-medium cursor-pointer">
                  Automatically create leads from emails
                </Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, incoming emails matching your filter rules will automatically create
                  new leads.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {!hasFilters && (
          <div className="rounded-md bg-accent-orange-bg border border-accent-orange/30 p-4">
            <p className="text-sm text-accent-orange">
              Add at least one filter rule to enable automatic lead creation.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">Filter Rules</h2>
            <Button variant="outline" size="sm" onClick={addFilter}>
              <Plus aria-hidden="true" className="h-4 w-4 mr-1" />
              Add Rule
            </Button>
          </div>

          {filters.length === 0 ? (
            <div className="rounded-md border border-dashed p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No filter rules yet. Add a rule to match incoming emails.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filters.map((rule, index) => (
                <FilterRuleRow
                  key={index}
                  rule={rule}
                  index={index}
                  onUpdate={updateFilter}
                  onDelete={deleteFilter}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={!isDirty || isSaving}>
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}
